import React from 'react';
import { determineAssetTransformer } from '../../transformers/determineAssetTransformer';
import { contentfulContentWrapperToArticleProductWrapper } from '../../transformers/contentfulContentWrapperToArticleProductWrapper';
import { ArticleProductWithProvider } from '../ArticleProduct/ArticleProduct';
import { contentfulProductSummaryToArticleProductProps } from '../../transformers/contentfulProductSummaryToArticleProductProps';
import { TimelineItem, TimelineWrapper } from '../TimelineWrapper/TimelineWrapper';
import { contentfulVideoTimestampToTimelineItemProps } from '../../transformers/contentfulVideoTimestampToTimelineItemProps';
import { contentfulVideoTimelineToTimelineWrapperProps } from '../../transformers/contentfulVideoTimelineToTimelineWrapperProps';
import { ArticleRichTextLayoutBlockStyleProps } from './ArticleRichTextLayoutBlock/ArticleRichTextLayoutBlock';
import { ArticleProductWrapper } from '../ArticleProductWrapper/ArticleProductWrapper';
import { StackYouTubePlayer } from '@jbhi-fi/components/src/StackYouTubePlayer/StackYouTubePlayer';
import { YouTubeTransformer } from '@jbhi-fi/components/src/Contentful/transformers/YouTubeTransformer';
import { FormatForm } from '../FormatForm/FormatForm';
import { contentfulApiFormAssemblyFormToFormatForm } from '../../transformers/contentfulApiFormAssemblyFormToFormatForm';
import { CompetitionStatusGrid } from '@jbhi-fi/components/src/CompetitionStatusGrid/CompetitionStatusGrid';
import { CompetitionGridTransformer } from '@jbhi-fi/components/src/Contentful/transformers/CompetitionGridTransformer';
import { ContentBanner } from '@jbhi-fi/components/src/ContentBanner/ContentBanner';
import {
  ContentfulContactBlock,
  ContentfulEntry,
  ContentfulIcon,
  ContentfulPreezieBanner,
} from '@jbhi-fi/apis/src/contentful/types';
import { LoadingSkeletonProps } from '@jbhi-fi/components/src/LoadingSkeleton/Base/Base';
import { ButtonTransformer } from '@jbhi-fi/components/src/Contentful/transformers/ButtonTransformer';
import { Icon, IconName } from '@jbhi-fi/lanyard-ui/components/Icon';
import { AlignedButton } from '@jbhi-fi/components/src/AlignedButton/AlignedButton';
import { PreezieFinder } from '@jbhi-fi/components/src/PreezieFinder/PreezieFinder';
import { PreezieBannerTransformer } from '@jbhi-fi/components/src/Contentful/transformers/PreezieBannerTransformer';
import { ListingBlock } from '@jbhi-fi/components/src/ListingBlock';
import { ListingBlockTransformer } from '@jbhi-fi/components/src/Contentful/transformers/ListingBlockTransformer';
import { SideBySide } from '@jbhi-fi/components/src/SideBySide/SideBySide';
import { SideBySideTransformer } from '@jbhi-fi/components/src/Contentful/transformers/SideBySideTransformer';
import { ScrollTarget } from '@jbhi-fi/components/src/ScrollTarget/ScrollTarget';
import { AnchorTransformer } from '@jbhi-fi/components/src/Contentful/transformers/AnchorTransformer';
import { TableOfContents } from '@jbhi-fi/components/src/TableOfContents/TableOfContents';
import { TableOfContentsTransformer } from '@jbhi-fi/components/src/Contentful/transformers/TableOfContentsTransformer';
import { ButtonBlockTransformer } from '@jbhi-fi/components/src/Contentful/transformers/ButtonBlockTransformer';
import { MultiColumnEntry } from '@jbhi-fi/components/src/MultiColumnEntry/MultiColumnEntry';
import { MultiColumnEntryTransformer } from '@jbhi-fi/components/src/Contentful/transformers/MultiColumnEntryTransformer';
import { MultiColumnBlock } from '@jbhi-fi/components/src/MultiColumnBlock/MultiColumnBlock';
import { MultiColumnBlockTransformer } from '@jbhi-fi/components/src/Contentful/transformers/MultiColumnBlockTransformer';
import { PerksButtonBlock } from '../PerksButtonBlock/PerksButtonBlock';
import { ContentfulPagesList } from '@jbhi-fi/components/src/ContentfulPagesList/ContentfulPagesList';
import { PageListingTransformer } from '@jbhi-fi/components/src/Contentful/transformers/PageListingTransformer';
import { TabsBlock } from '@jbhi-fi/components/src/TabsBlock/TabsBlock';
import { ProductBlockTransformer } from '@jbhi-fi/components/src/Contentful/transformers/ProductBlockTransformer';
import { HeroBanner } from '@jbhi-fi/components/src/HeroBanner/HeroBanner';
import { LeaderboardBannerTransformer } from '@jbhi-fi/components/src/Contentful/transformers/LeaderboardBannerTransformer';
import { AccordionTransformer } from '@jbhi-fi/components/src/Contentful/transformers/AccordionTransformer';
import { ContentAccordion } from '@jbhi-fi/components/src/ContentAccordion/ContentAccordion';
import { determineCalloutTransformer } from '@jbhi-fi/components/src/Contentful/transformers/determineCalloutTransformer';
import { ContactBlockTransformer } from '@jbhi-fi/components/src/Contentful/transformers/ContactBlockTransformer';
import { ContentBannerTransformer } from '@jbhi-fi/components/src/Contentful/transformers/ContentBannerTransformer';
import { getSalesLineHours } from '@jbhi-fi/components/src/ContactBlock/utils/salesLine/salesLine';
import { GoToLink } from '@jbhi-fi/components/src/GoToLink/GoToLink';
import { Section } from '@jbhi-fi/components/src/Section/Section';
import { SectionTransformer } from '@jbhi-fi/components/src/Contentful/transformers/SectionTransformer';
import { determineCarouselTransformer } from '@jbhi-fi/components/src/Contentful/transformers/CarouselTransformer/determineCarouselTransformer';
import { serviceHelper } from '../../../../../config/service-helper';
import { determineContactBlock } from '@jbhi-fi/components/src/ContactBlock/determineContactBlock';

type EntryTransformersMap = Map<string, EntryTransformer | ((entityFields: any) => EntryTransformer)>;

let _cache: EntryTransformersMap | null = null;

export type EntryTransformer = {
  OutputComponent: React.FC<any>;
  transformer: (entity: any) => any;
  loadingProps?: LoadingSkeletonProps;
  layoutBlockProps?: ArticleRichTextLayoutBlockStyleProps | ((entry: any) => ArticleRichTextLayoutBlockStyleProps);
};

export const getEntryTransformersMap = (): EntryTransformersMap => {
  if (!_cache) {
    _cache = new Map(
      Object.entries({
        button: (entry: ContentfulEntry<any>) => {
          const isLinkWithoutIcon = entry?.fields?.type === 'Link' && !entry?.fields?.icon;
          return {
            OutputComponent: isLinkWithoutIcon ? GoToLink : AlignedButton,
            transformer: isLinkWithoutIcon ? ButtonTransformer.toGoToLinkProps : ButtonTransformer.toAlignedButtonProps,
            loadingProps: { height: '20px', width: '200px', shouldShimmer: false },
            layoutBlockProps: { paddingTop: 2 },
          };
        },
        carousel: determineCarouselTransformer<EntryTransformer>,
        Asset: determineAssetTransformer,
        featuredProductWrapper: {
          OutputComponent: ArticleProductWrapper,
          transformer: contentfulContentWrapperToArticleProductWrapper,
          loadingProps: { height: { xs: '300px', md: '500px' } },
          layoutBlockProps: {
            paddingLeft: { mobile: 0 },
            paddingRight: { mobile: 0 },
            paddingTop: 15,
            paddingBottom: 1,
          },
        },
        productSummary: {
          OutputComponent: ArticleProductWithProvider,
          transformer: contentfulProductSummaryToArticleProductProps,
        },
        videoTimeline: {
          loadingProps: { height: { xs: '300px', md: '500px' } },
          layoutBlockProps: { paddingTop: 10 },
          OutputComponent: TimelineWrapper,
          transformer: contentfulVideoTimelineToTimelineWrapperProps,
        },
        videoTimestamp: {
          layoutBlockProps: { paddingTop: 0, paddingLeft: 0, paddingRight: 0 },
          OutputComponent: TimelineItem,
          transformer: contentfulVideoTimestampToTimelineItemProps,
        },
        icon: {
          layoutBlockProps: { paddingTop: 0, paddingBottom: 0, paddingRight: 0, paddingLeft: 0 },
          OutputComponent: Icon,
          transformer: (entry: ContentfulIcon) => ({
            name: entry?.fields?.name as IconName,
            fallback: null,
            placeholder: null,
          }),
        },
        youTube: {
          layoutBlockProps: { paddingTop: 10 },
          loadingProps: { height: '560px', width: '315px' },
          OutputComponent: StackYouTubePlayer,
          transformer: YouTubeTransformer.toYouTubePlayerProps,
        },
        formAssemblyForm: {
          layoutBlockProps: { paddingTop: 7.5 },
          loadingProps: { height: '700px', width: '315px' },
          OutputComponent: FormatForm,
          transformer: contentfulApiFormAssemblyFormToFormatForm,
        },
        competitionGrid: {
          OutputComponent: CompetitionStatusGrid,
          transformer: CompetitionGridTransformer.toCompetitionStatusGridProps,
        },
        contentBanner: {
          OutputComponent: ContentBanner,
          transformer: ContentBannerTransformer.toContentBannerProps,
        },
        listingBlock: {
          OutputComponent: ListingBlock,
          layoutBlockProps: ListingBlockTransformer.toArticleRichTextLayoutBlockProps,
          transformer: ListingBlockTransformer.toListingBlockProps,
        },
        preezieBanner: {
          OutputComponent: PreezieFinder,
          transformer: (entry: ContentfulPreezieBanner) =>
            PreezieBannerTransformer.toPreezieFinderProps(entry, serviceHelper.preezieScriptUrl),
        },
        sideBySide: {
          OutputComponent: SideBySide,
          transformer: SideBySideTransformer.toSideBySideProps,
        },
        anchor: (entry: ContentfulEntry<any>) => {
          const isHeadingHidden = entry.fields?.showAs === 'hide';
          return {
            OutputComponent: ScrollTarget,
            transformer: AnchorTransformer.toScrollTargetProps,
            layoutBlockProps: isHeadingHidden
              ? { paddingTop: 0, paddingBottom: 0, paddingRight: 0, paddingLeft: 0 }
              : undefined,
          };
        },
        tableOfContents: {
          OutputComponent: TableOfContents,
          transformer: TableOfContentsTransformer.toTableOfContentsProps,
        },
        buttonBlock: {
          OutputComponent: PerksButtonBlock,
          transformer: ButtonBlockTransformer.toButtonBlockProps,
        },
        multiColumnEntry: {
          OutputComponent: MultiColumnEntry,
          transformer: MultiColumnEntryTransformer.toMultiColumnEntryProps,
          layoutBlockProps: { paddingBottom: 7.5 },
        },
        multiColumnBlock: {
          OutputComponent: MultiColumnBlock,
          transformer: MultiColumnBlockTransformer.toMultiColumnBlockProps,
          layoutBlockProps: { paddingBottom: 10 },
        },
        callout: determineCalloutTransformer,
        pageListing: {
          OutputComponent: ContentfulPagesList,
          transformer: PageListingTransformer.toContentfulPagesListProps,
        },
        productBlock: {
          OutputComponent: TabsBlock,
          transformer: ProductBlockTransformer.toTabsBlockProps,
        },
        leaderboardBanner: {
          OutputComponent: HeroBanner,
          transformer: LeaderboardBannerTransformer.toHeroBannerProps,
        },
        accordion: {
          OutputComponent: ContentAccordion,
          transformer: AccordionTransformer.toAccordionProps,
        },
        contactBlock: {
          OutputComponent: determineContactBlock(),
          transformer: (e: ContentfulContactBlock) =>
            ContactBlockTransformer.toContactBlockProps(e, {
              salesLineHours: getSalesLineHours(),
            }),
        },
        section: () => {
          return {
            OutputComponent: Section,
            transformer: (entry: ContentfulEntry<any>) =>
              SectionTransformer.toSectionProps(entry, {
                contentBlockProps: { paddingX: 0, paddingY: 0 },
              }),
          };
        },
      })
    );
  }
  return _cache;
};
